import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, Params, RedirectCommand, Router } from "@angular/router";
import { PendingSignInService } from "../../_shared/services/pending-sign-in.service";
import { hasLoginAsClaim } from "../../_shared/services/jwt-payload";
import { ADMIN_POST_SIGN_IN_PAGE, SIGN_IN_ROUTE } from "../post-sign-in-page";

const LOGIN_AS_PAGE = "broker-accounts";
export const NOT_STARTED_HERE =
    "Sign-in was stopped because it did not start in this browser tab. Please sign in again.";
// A page behind sign-in: a signed-in visitor stays in the user area and sees the reason there, and AuthGuard passes the
// reason on to the sign-in page for anyone else.
const REFUSAL_PAGE = "/";

export const signedInGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const query = route.queryParamMap;
    const token = query.get("token");
    // The nonce is checked before anything else: a return to this tab's sign-in spends it, with a token or with a
    // refusal. Any other address, a Login as token aside, is refused with our own reason, never the one it carries.
    const page =
        inject(PendingSignInService).complete(route.paramMap.get("nonce")) ??
        (token !== null && hasLoginAsClaim(token) ? LOGIN_AS_PAGE : null);
    if (page === null) {
        return redirect(router, REFUSAL_PAGE, { errorcode: NOT_STARTED_HERE });
    }

    if (!token) {
        // A refused sign-in comes back with its reason instead of a token. A sign-in for the admin hand-off was started
        // signed out, and its retry must come back to the hand-off too.
        const reason = { errorcode: query.get("errorcode"), errordescription: query.get("errordescription") };
        return page === ADMIN_POST_SIGN_IN_PAGE
            ? redirect(router, SIGN_IN_ROUTE, { page, ...reason })
            : redirect(router, REFUSAL_PAGE, reason);
    }

    localStorage.setItem("token", token);
    const userId = query.get("user_id");
    if (userId) {
        localStorage.setItem("userId", userId);
    }

    return redirect(router, `/${page}`, { USER_CREATED: query.get("USER_CREATED") });
};

// Replaced in history too, so Back does not return to the address of a stored token.
function redirect(router: Router, path: string, queryParams: Params): RedirectCommand {
    return new RedirectCommand(router.createUrlTree([path], { queryParams }), { replaceUrl: true });
}
